import { CartModal } from "@@/carts";
import { useModal } from "@@/modals/use-modal";
import { useCalculatedProviderStatus } from "@@/providers/calculate-provider-status";
import { CartProviderCheckModal } from "@@/providers/cart-provider-check-modal";
import { ProviderPauseCheckModal } from "@@/providers/provider-pause-check-modal";
import { _ProductImages } from "@@/routes/products/$productId/_bookable/generic/_product_images";
import { Conditional } from "@@/shared/conditional";
import { CurrentUserIcon } from "@@/shared/current-user-icon";
import {
    HorizontalDivider,
    HorizontalLine,
    VerticalDivider,
} from "@@/shared/dividers";
import { FlexColumn, FlexRow } from "@@/shared/flex-containers";
import { Icon } from "@@/shared/icons/icon";
import { Label } from "@@/shared/label";
import { LayoutCell } from "@@/shared/layout-cell";
import { LayoutGrid } from "@@/shared/layout-grid";
import { ImageV2 } from "@@/shared/pictures/image";
import { usePageBreakpoint } from "@@/shared/responsiveness/use-page-breakpoint";
import { TextBox } from "@@/shared/text";
import { useTheme } from "@emotion/react";
import {
    faClipboardCheck,
    faGift,
    faHeart,
} from "@fortawesome/pro-solid-svg-icons";
import type {
    PickedOptional,
    Provider,
    RemSize,
    OptionalAmount as TOptionalAmount,
    OptionalGroup as TOptionalGroup,
    Voucher_Product,
} from "@towni/common";
import {
    isAlfredItemActive,
    isVoucher,
    joinTranslatables,
    providerIdZodSchema,
    sumPrices,
    translation,
    zeroPrice,
} from "@towni/common";

import { useHomePath } from "@@/backoffice/for-providers/settings/settings/use-home-path";
import { AddMessageRow } from "@@/products";
import { useProductDetailsContext } from "@@/products/product-details-context";
import { _mapPickedOptionalsByOptionalGroupId } from "@@/products/vouchers/_map-picked-optionals-by-optional-group-id";
import { _BackButton } from "@@/routes/products/$productId/_bookable/generic/_back-button";
import { _ContentBlocks } from "@@/routes/products/$productId/_bookable/generic/_content-blocks";
import { useVoucherData } from "@@/routes/vouchers/$voucher/use-voucher-data";
import { AddToCartWithQuantity } from "@@/shared/buttons";
import { FadedBottomContainer } from "@@/shared/buttons/faded-bottom-container";
import { ForEach } from "@@/shared/for-each";
import { OptionalGroup } from "@@/shared/options";
import { OptionalAmount } from "@@/shared/options/optional-amount-group";
import { useNavigateTo } from "@@/shared/text/navigate-to";
import { PriceWithDiscountsTextBox } from "@@/shared/text/price-with-discounts-text-box";
import React, { useMemo } from "react";
import { useSearchParams } from "react-router";

type Props = {
    readonly product: Voucher_Product;
    readonly provider: Provider;
    readonly hideLinkToShop: boolean;
};

const VoucherPurchaseV2 = (props: Props) => {
    const theme = useTheme();

    const isMedium = usePageBreakpoint({ when: "M" });

    const _isMediumSmall = usePageBreakpoint({ when: "MS" });

    const isMobile = usePageBreakpoint({ when: "📱" });
    const { product, provider } = props;

    const providerStatus = useCalculatedProviderStatus(provider);

    const productDisabled =
        providerStatus === "PAUSED" ||
        (product.status && !isAlfredItemActive(product.status));

    const headerContent = (
        <>
            <_BackButton provider={provider} />
            <Conditional when={!isMobile}>
                <HorizontalDivider />
                <CurrentUserIcon />
            </Conditional>
        </>
    );

    const oneColumn = true; //isMobile || isMediumSmall;
    const isMobileOrMedium = isMobile || isMedium;

    return (
        <>
            <LayoutGrid
                css={{
                    label: "layout-grid",
                    marginTop: isMobileOrMedium ? 0 : 20,
                    marginBottom: isMobileOrMedium ? 0 : 100,

                    flex: 1,
                    backgroundColor: theme.colors.white.asString,
                    borderRadius: isMobileOrMedium ? 0 : 20,
                    gridTemplateColumns: oneColumn
                        ? "1fr"
                        : "minmax(0, 1fr) 50px minmax(0, 1fr)",
                    gridTemplateRows: "1fr",
                    gridAutoRows: "min-content",
                    paddingBottom: isMobileOrMedium ? 0 : 20, //
                    opacity: productDisabled ? 0.8 : 1,
                    filter: productDisabled ? "grayscale(100%)" : "unset",
                    pointerEvents: productDisabled ? "none" : "unset",

                    position: "relative",
                }}>
                <Conditional when={isMobile}>
                    {/* HEADER */}
                    <LayoutCell
                        css={{
                            padding: 20,
                            gridRow: 1,
                            label: "back_button_area",
                            display: "flex",
                            justifyContent: "space-between",
                            zIndex: 9999,
                            borderBottom: "none",
                            gridColumn: "1 / -1",
                            alignItems: "flex-start",
                            position: "sticky",
                            top: 0,
                            alignSelf: "start",
                            backgroundColor: theme.colors.transparent.asString,
                        }}>
                        {headerContent}
                    </LayoutCell>
                </Conditional>

                {/* IMAGES_AREA */}
                <LayoutCell
                    css={{
                        label: "images_area",
                        display: "flex",
                        justifyContent: "stretch",
                        alignTracks: "stretch",
                        gridColumn: "1 / -1",
                        gridRow: 1,
                        paddingTop: 0,
                        paddingLeft: 0,
                        paddingRight: 0,
                        position: "relative",
                        height: "fit-content",
                        overflow: "hidden",
                        backgroundColor:
                            theme.colors.default.background.asString,
                        borderRadius: isMobile ? 0 : 10,
                        ...(!isMobile
                            ? {
                                  marginTop: (isMedium ? 30 : 60) / 2,
                                  marginLeft: (isMedium ? 30 : 60) / 2,
                                  marginRight: (isMedium ? 30 : 60) / 2,
                                  marginBottom: (isMedium ? 30 : 40) / 2,
                              }
                            : {}),
                    }}>
                    <_ProductImages
                        product={product}
                        provider={provider}
                        fallbackOnProvider
                    />
                    <div
                        css={{
                            position: "absolute",
                            bottom: 20,
                            left: 20,
                            zIndex: 100,
                            label: "LOGO",
                        }}>
                        <ImageV2
                            imageSource={provider.logoImageRef}
                            title={joinTranslatables([
                                provider.meta.title,
                                " - Logo",
                            ])}
                            css={{
                                width: 60,
                                height: 60,
                                borderRadius: 3000,
                                overflow: "hidden",
                            }}
                        />
                    </div>
                </LayoutCell>

                {/* COLUMN_ONE */}
                <LayoutCell
                    css={{
                        gridColumn: 1,
                        label: "column_one",
                        paddingTop: 20,
                        paddingLeft: 20,
                        paddingRight: 20,
                        backgroundColor:
                            theme.colors.default.background.asString,
                        ...(!isMobile
                            ? {
                                  paddingLeft: 35, //isMedium ? 35 : 60,
                                  paddingRight: 35,

                                  paddingTop: 0,
                              }
                            : {}),
                    }}>
                    <Conditional when={productDisabled}>
                        <Label
                            text={translation({
                                sv: "Ej tillgänglig",
                                en: "Unavailable",
                            })}
                            colorTheme="YELLOW"
                            size={1.5}
                        />
                        <VerticalDivider L />
                    </Conditional>
                    <VoucherProductDetails
                        product={product}
                        provider={provider}
                    />

                    <VerticalDivider size={2} />
                    <HorizontalLine fillParentWidth />
                    <VerticalDivider S />

                    <VoucherPriceDetails provider={provider} />
                </LayoutCell>
            </LayoutGrid>

            <CartProviderCheckModal providerId={product?.providerId} />
            <ProviderPauseCheckModal providerId={product?.providerId} />
        </>
    );
};

const VoucherProductDetails = (props: {
    product: Voucher_Product;
    provider: Provider;
}) => {
    const theme = useTheme();
    const { product } = props;

    const contentBlocks = isVoucher(product.skus[0])
        ? product.skus[0].acquire.contentBlocks
        : [];

    return (
        <FlexColumn fillParentWidth tag="_column_one">
            {/* Kicker */}
            <Conditional
                when={!!product.meta.kicker}
                render={() => (
                    <TextBox
                        text={product.meta.kicker}
                        color={theme.colors.primary}
                        weight="900"
                        dataTestId="kicker"
                        size={0.8}
                        css={{
                            label: "kicker",
                        }}
                    />
                )}
            />
            <VerticalDivider XS />
            {/* Title */}
            <TextBox
                text={product.meta.title}
                size={2.2}
                weight="900"
                dataTestId="product_title"
                userSelect="text"
                css={{
                    label: "title",
                }}
            />
            <VerticalDivider S />
            {/* Subtitle */}
            <Conditional
                when={!!product.meta.subtitle}
                render={() => (
                    <>
                        <TextBox
                            text={product.meta.subtitle}
                            size={1.2}
                            dataTestId="product_subtitle"
                            color={theme.colors.black.light70}
                            weight="500"
                            userSelect="text"
                            css={{
                                label: "subtitle",
                            }}
                        />
                        <Conditional when={!!product.meta.description}>
                            <VerticalDivider M />
                            <HorizontalLine />
                            <VerticalDivider M />
                        </Conditional>
                    </>
                )}
            />

            {/* Description */}
            <Conditional
                when={!!product.meta.description}
                render={() => (
                    <>
                        <TextBox
                            text={product.meta.description}
                            userSelect="text"
                            dataTestId="product_description"
                            lineClamp={{
                                lines: 5,
                                expandable: true,
                            }}
                            size={1}
                            renderMarkdown={true}
                            css={{
                                label: "description",
                            }}
                        />
                        <Conditional when={contentBlocks.length > 0}>
                            <VerticalDivider M />
                            <HorizontalLine />
                            <VerticalDivider M />
                        </Conditional>
                    </>
                )}
            />

            {/* Info block */}
            <Conditional when={contentBlocks.length > 0}>
                <_ContentBlocks product={product} />
            </Conditional>
        </FlexColumn>
    );
};

const VoucherPriceDetails = ({ provider }: { provider: Provider }) => {
    const theme = useTheme();
    const [{ show }, modalId] = useModal("cart_modal__");
    const state = useProductDetailsContext();
    const navigateTo = useNavigateTo();

    const [searchParams] = useSearchParams();
    const { optionalGroups } = useVoucherData();
    const referrer = searchParams.get("ref");
    const referredFromProviderId = providerIdZodSchema.safeParse(
        searchParams.get("refPID"),
    );

    const messageToProvider =
        useProductDetailsContext(context => context.messageToProvider) ?? "";
    const setMessageToProvider = useProductDetailsContext(
        context => context.setMessageToProvider,
    );

    const cartItem = state.getOrderItems();

    const currentPrice = cartItem
        ? sumPrices(cartItem.map(p => p.price))
        : null;

    const providerHomePath = useHomePath(provider);
    const afterAddToCartPath = useMemo(() => {
        if (
            referredFromProviderId.success &&
            referredFromProviderId.data !== provider._id
        ) {
            // Referred from another providers page, or from unknown
            // Try to find the products providers default page
            return providerHomePath + "#goback";
        }
        if (referrer) {
            // Referred from another page
            return referrer;
        }

        return providerHomePath + "#goback";

        // return (provider?.slug ? `/shop/${provider.slug}` : "/") + "#goback";
    }, [provider, providerHomePath, referrer, referredFromProviderId]);

    const onChange =
        (optionalGroup: TOptionalGroup | TOptionalAmount) =>
        (pickedOptionals: PickedOptional[]) => {
            const optionalsToKeep =
                state.pickedOptionals?.filter(
                    picked =>
                        picked.optional.optionalGroupId !== optionalGroup._id,
                ) ?? [];
            const update = [...optionalsToKeep, ...pickedOptionals];
            state.setPickedOptionals(update);
        };

    const pickedOptionsMap = React.useMemo(
        _mapPickedOptionalsByOptionalGroupId(state.pickedOptionals ?? []),
        [state.pickedOptionals],
    );

    React.useEffect(
        function checkIfAddToCartButtonShouldBeDisabled() {
            const addToCartDisabled =
                !state.selectedSku ||
                // where there is an option group...
                !!optionalGroups
                    // with a required minimal number of options...
                    .some(optionalGroupOrOptionalAmount => {
                        if (!optionalGroupOrOptionalAmount.minMax.min)
                            return false; // undefined or 0
                        const picked = pickedOptionsMap.get(
                            optionalGroupOrOptionalAmount._id,
                        );
                        if (!picked) return true;

                        const notEnough =
                            picked.length <
                            optionalGroupOrOptionalAmount.minMax.min;
                        return notEnough;
                    });

            if (addToCartDisabled !== state.addToCartDisabled) {
                // Set button as disabled
                state.setAddToCartDisabled(addToCartDisabled);
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [
            state.selectedSku,
            state.addToCartDisabled,
            state.setAddToCartDisabled,
            optionalGroups,
            pickedOptionsMap,
        ],
    );

    return (
        <>
            <FlexColumn
                grow={1}
                shrink={0}
                mainAxis="space-between"
                background={{
                    color: theme.colors.white.asString,
                }}
                crossAxis="stretch">
                <FlexColumn css={{ flexGrow: 1, flexShrink: 0 }}>
                    <Conditional when={optionalGroups.length > 0}>
                        <ForEach
                            divider={<VerticalDivider />}
                            itemOf={Array.from(
                                optionalGroups?.values() || [],
                            ).reduce(
                                (agg, next) => {
                                    // Sort required first
                                    if (next.minMax.min ?? 0 > 0)
                                        agg.unshift(next);
                                    else agg.push(next);
                                    return agg;
                                },
                                [] as (TOptionalGroup | TOptionalAmount)[],
                            )}>
                            {optionalGroup => {
                                switch (optionalGroup._type) {
                                    case "OPTIONAL_AMOUNT": {
                                        return (
                                            <OptionalAmount
                                                optionalGroup={optionalGroup}
                                                onChange={onChange(
                                                    optionalGroup,
                                                )}
                                                padding={{
                                                    leftRight: 20,
                                                    top: 20,
                                                }}
                                                optionPadding={{
                                                    leftRight: 20,
                                                    topBottom: 15,
                                                }}
                                            />
                                        );
                                    }
                                    case "OPTIONAL_GROUP":
                                    default:
                                        return (
                                            <OptionalGroup
                                                key={optionalGroup._id}
                                                padding={{
                                                    leftRight: 20,
                                                    top: 20,
                                                }}
                                                optionPadding={{
                                                    leftRight: 20,
                                                    topBottom: 15,
                                                }}
                                                initialPickedOptions={state.pickedOptionals
                                                    ?.filter(
                                                        item =>
                                                            item.optional
                                                                .optionalGroupId ===
                                                            optionalGroup._id,
                                                    )
                                                    .map(
                                                        item =>
                                                            item.optional._id,
                                                    )}
                                                optionalGroup={optionalGroup}
                                                onChange={onChange(
                                                    optionalGroup,
                                                )}
                                            />
                                        );
                                }
                            }}
                        </ForEach>

                        <VerticalDivider size={2} />
                        <HorizontalLine />
                    </Conditional>
                    <AddMessageRow
                        onSetMessage={setMessageToProvider}
                        initialMessage={messageToProvider}
                    />
                    <HorizontalLine />

                    <FlexRow padding={{ all: 20 }} mainAxis="space-between">
                        <TextBox
                            text={translation({
                                sv: "Totalt",
                                en: "Total",
                                de: "Gesamt",
                            })}
                        />
                        <PriceWithDiscountsTextBox
                            price={currentPrice || zeroPrice}
                            discounts={[]}
                            fractions="NONE"
                            dataTestId="tbx-total-price"
                            reversed
                        />
                    </FlexRow>
                    <FlexColumn fillParentWidth padding={{ leftRight: 20 }}>
                        <VerticalDivider M />
                        <UspBlock />
                        {/*TO BE FIXED NEED FOR FADED BOTTOMCONTAINER*/}
                        {/*WHY I NEED IT??? BAD DEVELOPER*/}
                        <VerticalDivider XL />
                        <VerticalDivider XL />
                        <VerticalDivider XL />
                    </FlexColumn>
                </FlexColumn>
            </FlexColumn>
            <FadedBottomContainer position="sticky" padding={{ leftRight: 20 }}>
                <AddToCartWithQuantity
                    afterAddNavigation={() => {
                        // If the provider uses the provider cart for vouchers, navigate to the page
                        //Else show the product cart.
                        if (provider.options.useProviderCartForVoucher) {
                            navigateTo(afterAddToCartPath);
                        } else {
                            show();
                        }
                    }}
                    clearBeforeAdding={false}
                    addButtonText={
                        provider.options.useProviderCartForVoucher
                            ? translation({
                                  sv: "Lägg i kundvagn",
                                  en: "Add to cart",
                              })
                            : translation({
                                  sv: "Köp",
                                  en: "Buy",
                              })
                    }
                    padding={{
                        bottom: 20,
                    }}
                />
            </FadedBottomContainer>
            <CartModal modalId={modalId} />
        </>
    );
};

const UspBlock = () => {
    const theme = useTheme();
    return (
        <FlexColumn>
            <FlexRow crossAxis="flex-start">
                <FlexColumn width="28px" crossAxis="center">
                    <Icon
                        fixedWidth
                        icon={faClipboardCheck}
                        color={theme.colors.primary}
                        size="M"
                        padding={{ top: 2 }}
                    />
                </FlexColumn>
                <HorizontalDivider />
                <FlexColumn width="100%">
                    <FlexRow>
                        <TextBox
                            text={translation({
                                sv: "Snabbt och tryggt",
                                en: "Delivered instantly",
                            })}
                            userSelect="text"
                            size={1}
                            weight={"700"}
                        />
                    </FlexRow>
                    <VerticalDivider XXS />
                    <TextBox
                        text={translation({
                            sv: "Gör köp direkt här, med den betalmetod som passar dig. Enkelt och säkert.",
                            en: "Buy directly here, with the payment method that suits you. Simple and secure.",
                        })}
                        userSelect="text"
                        size="S"
                        weight="400"
                        color={theme.colors.black.light30}
                        css={{
                            lineHeight: 1.3,
                        }}
                    />
                    <VerticalDivider M />
                </FlexColumn>
            </FlexRow>
            <FlexRow crossAxis="flex-start">
                <FlexColumn width="28px" crossAxis="center">
                    <Icon
                        icon={faGift}
                        fixedWidth
                        color={theme.colors.primary}
                        size="M"
                        padding={{ top: 2 }}
                    />
                </FlexColumn>
                <HorizontalDivider />
                <FlexColumn width="100%">
                    <FlexRow>
                        <TextBox
                            text={translation({
                                sv: "Flexibel leverans",
                                en: "Flexible delivery",
                            })}
                            userSelect="text"
                            size={1}
                            weight={"700"}
                        />
                    </FlexRow>
                    <VerticalDivider XXS />
                    <TextBox
                        text={translation({
                            sv: "Oavsett om det är digitalt eller fysiskt – vi ser till att ni får er beställning på ett enkelt och tryggt sätt.",
                            en: "Whether it's digital or physical - we make sure you get your order in a simple and secure way.",
                        })}
                        userSelect="text"
                        size="S"
                        weight="400"
                        color={theme.colors.black.light30}
                        css={{
                            lineHeight: 1.3,
                        }}
                    />
                    <VerticalDivider M />
                </FlexColumn>
            </FlexRow>
            <FlexRow crossAxis="flex-start">
                <FlexColumn width="28px" crossAxis="center">
                    <Icon
                        fixedWidth
                        icon={faHeart}
                        color={theme.colors.primary}
                        size={1 as RemSize}
                        padding={{ top: 2 }}
                    />
                </FlexColumn>
                <HorizontalDivider />
                <FlexColumn width="100%">
                    <FlexRow>
                        <TextBox
                            text={translation({
                                sv: "Enkelt att dela eller ge bort",
                                en: "Easy to share or give away",
                            })}
                            userSelect="text"
                            size={1}
                            weight={"700"}
                        />
                    </FlexRow>
                    <VerticalDivider XXS />
                    <TextBox
                        text={translation({
                            sv: "Ni kan direkt dela presentkortet och den som tar emot får en fin länk direkt till gåvan. Går också att skriva ut (och slå in med knorr!)",
                            en: "You can directly share the gift card and the recipient gets a nice link directly to the gift. Can also be printed (and wrapped with a bow!)",
                        })}
                        userSelect="text"
                        size="S"
                        weight="400"
                        color={theme.colors.black.light30}
                        css={{
                            lineHeight: 1.3,
                        }}
                    />
                </FlexColumn>
            </FlexRow>
        </FlexColumn>
    );
};

export { VoucherPurchaseV2 };
